import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { FaInfoCircle } from 'react-icons/fa';
import { RiEye2Line, RiEyeCloseLine } from 'react-icons/ri';
import { validatePasswordFormat } from 'utils/validation';
import ROUTES from 'constants/routes';
import Panel from 'components/Panel';
import Grid from '@material-ui/core/Grid';
import { FieldGroup } from 'components/Field';
import TextField, { FIELD_TYPES } from 'components/Mui/TextField';
import AppLogo, { appLogoVersions } from 'components/AppLogo';
import { BRIGHT_GRAY } from 'constants/colors';
import Button, { buttonColors, ButtonGroup, buttonSizes } from 'components/Button';
import Form from 'components/Form';
import { FormWrap, LogoWrap, FormLink } from '../styles';
import { initContainer, exitContainer, changePassword } from './actions';
import { getChangeRequired, getIsSubmitting } from './selectors';
import { RequirementsList } from './styles';

const ViewPasswordIcon = ({ view, ...rest }) =>
  view ? <RiEye2Line {...rest} /> : <RiEyeCloseLine {...rest} />;

ViewPasswordIcon.propTypes = {
  view: PropTypes.bool,
};

ViewPasswordIcon.defaultProps = {
  view: true,
};

const NewPassword = ({
  goToLogin,
  onEnter,
  onExit,
  changeRequired,
  onChangePassword,
  isSubmitting,
}) => {
  const [showRequirements, setShowRequirements] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPasswordError, setNewPasswordError] = useState(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const passwordFieldType = passwordVisible ? FIELD_TYPES.TEXT : FIELD_TYPES.PASSWORD;

  const resetErrors = () => {
    setNewPasswordError(null);
    setConfirmPasswordError(null);
  };

  const handleOnNewPasswordChange = value => {
    resetErrors();
    setNewPassword(value);
  };

  const handleOnConfirmPasswordChange = value => {
    resetErrors();
    setConfirmPassword(value);
  };

  const validateForm = () => {
    let isValid = true;
    const isValidNewPassword = validatePasswordFormat(newPassword);

    if (!isValidNewPassword) {
      setNewPasswordError('Invalid password format. Check the password requirements.');
      isValid = false;
    }
    if (confirmPassword !== newPassword) {
      setConfirmPasswordError("Doesn't match the new password.");
      isValid = false;
    }

    return isValid;
  };

  const handleOnSubmit = () => {
    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    onChangePassword(newPassword);
  };

  useEffect(() => {
    onEnter();

    return () => {
      onExit();
    };
  }, [onEnter, onExit]);

  return (
    <Panel>
      <FormWrap>
        <LogoWrap>
          <AppLogo
            color={BRIGHT_GRAY}
            version={appLogoVersions.v2}
            link='https://commerceblitz.com'
          />
        </LogoWrap>
        <Form onSubmit={handleOnSubmit}>
          {changeRequired && (
            <>
              <Grid
                container
                justifyContent="center"
                style={{ padding: '1rem 0' }}
              >
                <h2>Update your password</h2>
              </Grid>
              <Grid
                container
                justifyContent="center"
                style={{ padding: '0 0 0.5rem 0' }}
              >
                <span>
                  {`
                    You need to update your password because 
                    this is the first time you are signing in,
                    or because your password has expired.
                  `}
                </span>
              </Grid>
            </>
          )}
          {!changeRequired && (
            <Grid
              container
              justifyContent="center"
              style={{ padding: '1rem 0' }}
            >
              <h2>Change your password</h2>
            </Grid>
          )}
          <Grid
            container
            justifyContent="center"
            style={{ padding: '0 0 0.5rem 0' }}
          >
            <FormLink onClick={() => setShowRequirements(!showRequirements)}>
              <span>
                {showRequirements ? 'Hide Password Requirements' : 'Show Password Requirements'}
              </span>
              <FaInfoCircle size="1rem" />
            </FormLink>
          </Grid>
          {showRequirements && (
            <Grid
              container
              style={{ padding: '0 0 1rem 0' }}
            >
              <RequirementsList>
                <li>Between 12 and 99 characters long.</li>
                <li>At least 1 Uppercase letter.</li>
                <li>At least 1 Lowercase letter.</li>
                <li>At least 1 Number.</li>
                <li>
                  <span>At least 1 of the following special characters:&nbsp;</span>
                  <span>
                    <strong>{'=+-^$*.[]{}()?"!@#%&/\\,><\':;|_~`'}</strong>
                  </span>
                </li>
              </RequirementsList>
            </Grid>
          )}

          <Grid
            container
            style={{ padding: '0 0 0.5rem 0' }}
          >
            <Button
              type="button"
              color={buttonColors.ghost}
              onClick={() => setPasswordVisible(!passwordVisible)}
              fullWidth
            >
              <span>Toggle password visibility</span>
              <ViewPasswordIcon
                view={passwordVisible}
                size="1rem"
              />
            </Button>
          </Grid>
          <Grid
            container
            alignItems="center"
            style={{ flexWrap: 'nowrap' }}
          >
            <TextField
              variant="outlined"
              type={passwordFieldType}
              label="New Password"
              placeholder="Enter New Password"
              value={newPassword}
              onChange={handleOnNewPasswordChange}
              error={newPasswordError}
              fullWidth
            />
          </Grid>
          <Grid
            container
            style={{ flexWrap: 'nowrap', padding: '0.5rem 0' }}
          >
            <FieldGroup>
              <TextField
                variant="outlined"
                type={passwordFieldType}
                label="Confirm New Password"
                value={confirmPassword}
                onChange={handleOnConfirmPasswordChange}
                error={confirmPasswordError}
                fullWidth
              />
            </FieldGroup>
          </Grid>

          <Grid container>
            <ButtonGroup>
              <Button
                type="submit"
                color={buttonColors.success}
                size={buttonSizes.large}
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                <span>Change Password</span>
              </Button>
            </ButtonGroup>
          </Grid>
        </Form>
        {!changeRequired && (
          <Grid
            container
            justifyContent="center"
            style={{ padding: '0.5rem 0' }}
          >
            <span>Already have login and password? &nbsp;</span>
            <FormLink onClick={() => goToLogin()}>Sign in</FormLink>
          </Grid>
        )}
      </FormWrap>
    </Panel>
  );
};

NewPassword.propTypes = {
  goToLogin: PropTypes.func.isRequired,
  onEnter: PropTypes.func.isRequired,
  onExit: PropTypes.func.isRequired,
  changeRequired: PropTypes.bool.isRequired,
  onChangePassword: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

const mapStateToProps = state => ({
  changeRequired: getChangeRequired(state),
  isSubmitting: getIsSubmitting(state),
});

const mapDispatchToProps = {
  goToLogin: () => push(ROUTES.LOGIN.BASE),
  onEnter: initContainer,
  onExit: exitContainer,
  onChangePassword: changePassword,
};

export default connect(mapStateToProps, mapDispatchToProps)(NewPassword);
